import {
  cn,
  createDataTableColumnHelper,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Link as UiLink,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { useRouteOverlayOpen } from "#/hooks/use-route-overlay-open";
import type { LesadministratieStatKpi } from "#/modules/lesadministratie.statistics/lesadministratie-stat-kpi";
import { getLessonAdministrationKpiData } from "#/modules/lesadministratie.statistics/lesson-administration.functions";
import { m } from "#/paraglide/messages";
import type { LesadministratieStatistiekenLeerling } from "#/schema.nswag";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/lesson-administration/stats/$kpi",
)({
  // No search params of its own — `klasIds`/`from`/`to` are inherited from the parent
  // `lesson-administration` route's `validateSearch`. Narrowed to exactly the keys the loader uses.
  loaderDeps: ({ search }) => ({ klasIds: search.klasIds, from: search.from, to: search.to }),
  loader: ({ deps, params }) =>
    getLessonAdministrationKpiData({
      data: {
        filters: deps,
        kpi: params.kpi,
        locale: params.locale,
        school: params.school,
        schoolyear: params.schoolyear,
      },
    }),
  component: LessonAdministrationStatsKpiPage,
  errorComponent: LessonAdministrationStatsKpiError,
});

function kpiTitle(kpi: LesadministratieStatKpi): string {
  switch (kpi) {
    case "afwezigheden":
      return m["lesadministratie.kpi_afwezigheden"]();
    case "blessures":
      return m["lesadministratie.col_injury"]();
    case "gymkleding-vergeten":
      return m["lesadministratie.kpi_gymkleding"]();
    case "disciplinaire-maatregel":
      return m["lesadministratie.kpi_discipline"]();
  }
}

const helper = createDataTableColumnHelper<LesadministratieStatistiekenLeerling>();

const columns = helper.columns([
  helper.accessor("naam", {
    header: () => m["common.fields.student"](),
    meta: { rowTarget: true, cellClassName: "font-medium" },
    cell: ({ row, getValue }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
        params={(prev: Record<string, string>) => ({ ...prev, id: row.original.leerlingId })}
      >
        {getValue()}
      </UiLink>
    ),
  }),
  helper.accessor("afwezigheid", {
    header: () => m["lesadministratie.col_absence"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("blessures", {
    header: () => m["lesadministratie.col_injury"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("gymkleding", {
    header: () => m["common.labels.gymkleding"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("korteTimeout", {
    header: () => m["common.labels.korte_timeout"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("langeTimeout", {
    header: () => m["common.labels.lange_timeout"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("uitsluitingLes", {
    header: () => m["common.labels.uitsluiting_les"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
  helper.accessor("timeoutsTotaal", {
    header: () => m["lesadministratie.col_timeouts_totaal"](),
    meta: {
      headerClassName: "min-w-[8ch] tabular-nums",
      cellClassName: "text-muted-foreground text-sm tabular-nums",
    },
  }),
]);

function LessonAdministrationStatsKpiPage() {
  const { locale, schoolyear, school } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const dialog = useRouteOverlayOpen(() => {
    void navigate({
      to: "/$locale/{-$schoolyear}/$school/insights/lesson-administration",
      params: { locale, schoolyear, school },
      search: (prev) => ({ ...prev }),
      replace: true,
      resetScroll: false,
      viewTransition: true,
    });
  });

  const title = kpiTitle(loaderData.kpi);

  return (
    <>
      <Dialog {...dialog}>
        <DialogContent
          className={cn({
            "md:max-w-lg!": loaderData.data.leerlingen.length === 0,
          })}
          showCloseButton
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {m["common.counts.students_of_total"]({
                count: loaderData.data.leerlingen.length,
                total: loaderData.data.totalLeerlingen,
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="no-scrollbar -mx-6 max-h-[50vh] overflow-y-auto px-6">
            {loaderData.data.leerlingen.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {m["common.empty_state.students_in_category"]()}
              </p>
            ) : (
              <DataTable
                size="small"
                aria-label={m["common.aria.students_in_count"]()}
                className="border-0"
                columns={columns}
                data={loaderData.data.leerlingen}
                getRowId={(row) => row.leerlingId}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Outlet />
    </>
  );
}

function LessonAdministrationStatsKpiError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
